import { memo, type CSSProperties } from 'react';

import {
  PROGRESS_BAR_HEIGHT_COMPACT,
  PROGRESS_BAR_HEIGHT_KANBAN,
  PROGRESS_BAR_HEIGHT_TABLE,
  PROGRESS_BAR_MIN_WIDTH_COMPACT,
  PROGRESS_BAR_MIN_WIDTH_TABLE,
} from '@/constants/ui';
import { STATUS_COLORS, type TaskStatus } from '@/types';

import styles from './TaskProgressBar.module.css';

interface TaskProgressBarProps {
  readonly progress: number;
  readonly status: TaskStatus;
  readonly variant?: 'table' | 'compact' | 'kanban';
}

const HEIGHT_MAP = {
  table: PROGRESS_BAR_HEIGHT_TABLE,
  compact: PROGRESS_BAR_HEIGHT_COMPACT,
  kanban: PROGRESS_BAR_HEIGHT_KANBAN,
} as const;

const MIN_WIDTH_MAP = {
  table: PROGRESS_BAR_MIN_WIDTH_TABLE,
  compact: PROGRESS_BAR_MIN_WIDTH_COMPACT,
  kanban: 0,
} as const;

const CONTAINER_CLASS_MAP = {
  table: styles.containerTable,
  compact: styles.containerCompact,
  kanban: styles.containerKanban,
} as const;

export const TaskProgressBar = memo(function TaskProgressBar({
  progress,
  status,
  variant = 'table',
}: TaskProgressBarProps) {
  const fillColor = STATUS_COLORS[status];
  const barHeight = HEIGHT_MAP[variant];
  const barMinWidth = MIN_WIDTH_MAP[variant];
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <span className={CONTAINER_CLASS_MAP[variant]}>
      <span
        className={styles.track}
        style={
          {
            '--bar-height': `${barHeight}px`,
            '--bar-min-width': `${barMinWidth}px`,
          } as CSSProperties
        }
      >
        <span
          className={styles.fill}
          style={
            {
              '--fill-color': fillColor,
              '--fill-width': `${clampedProgress}%`,
            } as CSSProperties
          }
        />
      </span>
      {variant === 'table' && (
        <span className={styles.text}>{clampedProgress}%</span>
      )}
    </span>
  );
});
